/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { partition } from 'lodash';
import concat from 'lodash/concat';
import delay from 'lodash/delay';
import differenceBy from 'lodash/differenceBy';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';

import { axios } from '@/services/axios';
import { downloadCSV } from '@/utils/utils';

import {
    CSV_TYPE,
    CSV_TYPE_ERROR,
    CSV_TYPE_FX,
    FIELDS_CHANGE_NEED_VALIDATION,
    FIELDS_CHANGE_NO_NEED_VALIDATION,
    IGNORED_MESSAGE,
    INVALID_PRIORITY_ERROR,
    INVALID_RATE_ERROR,
    INVALID_RATE_NAME,
    NO_DATA_FOUND_ERROR,
    PREFIX_ERROR,
    RATE_NOT_ACTIVE,
    SUCCESS_MESSAGE,
    RATE_DELETED,
} from './constants';

import {
    haveErrorsFilter,
    isNegativeRate,
    dataIsUnchangedFilter,
} from './helper';
import useCSVTemplate from './useCSVTemplate';

let VERIFICATION_CHECKS = [
    {
        label: 'Received CSV',
        check: false,
        current: true,
        completionCounter: 0,
        total: 1,
    },
    {
        label: 'Fetching Calling Rates',
        check: false,
        current: false,
        completionCounter: 0,
        total: 4,
    },
    {
        label: 'Converting CSV',
        check: false,
        current: false,
        completionCounter: 0,
        total: 3,
    },
    {
        label: 'Verifying Calling Rates ID',
        check: false,
        current: false,
        completionCounter: 0,
        total: 5,
    },
    {
        label: 'Verifying Deleted Rate',
        check: false,
        current: false,
        completionCounter: 0,
        total: 4,
    },
];

const DEFAULT_JSON_DATA = {
    errors: [],
    ignored: false,
    isNewRate: false,
};

const RATES = [
    'flagFallInRate',
    'flagFallOutRate',
    'inboundRate',
    'outboundRate',
];

export const SHARED_FIELDS_TO_EXPORT = {
    id: 'Rate ID',
    name: 'Rate Name',
    priority: 'Priority',
    prefix: 'Prefix',
    flagFallInRate: 'Flag Fall In Rate',
    flagFallOutRate: 'Flag Fall Out Rate',
    inboundRate: 'Inbound Rate',
    outboundRate: 'Outbound Rate',
    isActive: 'Active', // isActive: true -> 1, isActive: false -> 0 (deleted)
};

export const FIELDS_TO_EXPORT = {
    ...SHARED_FIELDS_TO_EXPORT,
    errors: 'Errors',
};

export const API_RESULT_FIELDS_TO_EXPORT = {
    ...SHARED_FIELDS_TO_EXPORT,
    apiErrors: 'Errors',
};

const INVALID_DATA_FILENAME = 'InvalidData.csv';

export const DEFAULT_VERIFICATION_RESULT = {
    passed: {
        columns: [],
        data: [],
    },
    failed: {
        columns: [],
        data: [],
    },
};

export default function useCSVCallingRates() {
    const [verificationChecks, setVerificationChecks] = useState([]);
    const [uploadData, setUploadData] = useState(null);
    const [dataJSON, setDataJSON] = useState(null);
    const [lookup, setLookup] = useState({
        currentRatesLookup: {},
    });
    const [payloads, setPayloads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [verificationResult, setVerificationResult] = useState(
        DEFAULT_VERIFICATION_RESULT,
    );
    const { planID } = useCSVTemplate();

    /**
     * for UI Validation progress checks, sets the next index to be in progress.
     * @param {*} i index in VERIFICATION_CHECKS that needs to be updated.
     */
    const changeVerificationChecks = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    newVerificationChecks[index].check = true;
                    if (index < newVerificationChecks.length - 1) {
                        newVerificationChecks[index + 1].current = true;
                    }
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };

    /**
     * for UI Validation progress checks, increments current index's completionCounter.
     * @param {*} i index in VERIFICATION_CHECKS that needs to be updated.
     */
    const changeVerificationProgresses = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const newVerificationChecks = [...prev];
                    newVerificationChecks[index].completionCounter += 1;
                    return newVerificationChecks;
                });
            },
            750,
            i,
        );
    };

    const fetchRateData = async () => {
        try {
            const ratesResponse = await axios.get(`/rates/${planID}`);
            changeVerificationProgresses(1);
            const newLookup = {
                currentRatesLookup: keyBy(ratesResponse.data, 'id'),
            };
            changeVerificationProgresses(1);
            changeVerificationChecks(1);
            setLookup(newLookup);
        } catch (e) {
            setError(e);
            console.error(e);
            throw new Error(e);
        }
    };

    //file change handler to Upload CSV
    const handleUploadData = (e) => {
        setVerificationChecks(VERIFICATION_CHECKS);
        if (
            e.target.files[0].type === CSV_TYPE ||
            e.target.files[0].type === CSV_TYPE_FX
        ) {
            const data = e.target.files[0];
            //save data
            setLoading(true);
            setUploadData(data);
            changeVerificationProgresses(0);
            changeVerificationChecks(0);

            //fetch data
            fetchRateData();
        } else {
            window.alert(CSV_TYPE_ERROR);
        }
    };
    //reset the whole UI states
    const handleReset = () => {
        setUploadData(null);
        setDataJSON(null);
        setVerificationResult(DEFAULT_VERIFICATION_RESULT);
        setPayloads([]);
        setLoading(false);
        setError('');
        setLookup({
            currentRatesLookup: {},
        });
        setVerificationChecks((prev) => {
            const newVerificationChecks = [...prev];
            newVerificationChecks.forEach((v, index) => {
                v.check = false;
                v.current = index === 0; // NOTE: restart at Received CSV check
                v.completionCounter = 0;
            });
            return newVerificationChecks;
        });
    };

    /**
     * use papaparse to convert CSV -> JSON (with webworker).
     * @param {*} data CSV data to be converted to JSON.
     * @returns JSON data
     */
    const convertCSVDataToJSON = async (data) => {
        return new Promise((resolve, reject) => {
            const papaparseConfig = {
                dynamicTyping: true, //for numbers type
                header: true, //CSV has headers
                worker: true, //use worker thread
                complete: function (results) {
                    return resolve(results);
                },
            };
            Papa.parse(data, papaparseConfig);
        });
    };

    /**
     * aggregates data with 0 errors with unlicensedUsersChanged into allSucceedData,
     * aggregates all data with errors & sort them by UPN,
     * aggregates all ignored users with allSucceedData & sort them by (verificationStatus, UPN)
     * @param {data, unlicensedUsersChanged, ignored} param0
     * @returns {allSucceedData}
     */
    const reportResult = ({
        noNeedValidationWithErrors,
        needValidationWithErrors,
        unchanged,
    }) => {
        const failed = concat(
            noNeedValidationWithErrors.filter(haveErrorsFilter),
            needValidationWithErrors.filter(haveErrorsFilter),
        );
        const success = differenceBy(
            concat(noNeedValidationWithErrors, needValidationWithErrors),
            failed,
            'id',
        );
        setVerificationResult((prev) => {
            const newVerificationResult = { ...prev };
            newVerificationResult.passed.data = orderBy(
                concat(unchanged, success).map((v) => ({
                    ...v,
                    flagFallInRate: v.flagFallInRate ?? 0,
                    flagFallOutRate: v.flagFallOutRate ?? 0,
                    inboundRate: v.inboundRate ?? 0,
                    outboundRate: v.outboundRate ?? 0,
                    verificationStatus: v.ignored
                        ? IGNORED_MESSAGE
                        : v.isActive
                          ? SUCCESS_MESSAGE
                          : RATE_DELETED,
                })),
                ['verificationStatus', 'name'],
                ['desc', 'asc'],
            );
            newVerificationResult.failed.data = orderBy(
                failed,
                ['name'],
                ['asc'],
            );
            return newVerificationResult;
        });

        return { allSucceedData: success };
    };

    //convert CSV columns into friendlier backend's JSON field
    const convertCSVHeaders = (csv) => {
        return csv.map((row) => {
            let newRow = { ...DEFAULT_JSON_DATA };
            Object.entries(SHARED_FIELDS_TO_EXPORT).forEach(
                ([beField, csvColumn]) => {
                    newRow[beField] = row[csvColumn];
                },
            );
            newRow.isNewRate = !lookup.currentRatesLookup[newRow.id];
            newRow.isActive = newRow?.isNewRate
                ? true
                : Boolean(newRow.isActive);
            newRow.prefix = String(newRow.prefix ?? '');
            return newRow;
        }).filter((row) => {
            // Filter out completely empty rows (no id, name, priority, or prefix)
            return row.id || row.name || row.priority || row.prefix;
        });
    };

    /**
     * transforms BE JSON fields into more human readable CSV heading columns.
     * @param {*} data JSON data to be transformed.
     * @returns JSON data with CSV heading.
     */
    const transformJSONToCSV = (data) => {
        return data.map((rate) => {
            return Object.keys(API_RESULT_FIELDS_TO_EXPORT).reduce(
                (acc, key) => {
                    const value = rate[key];
                    const newKey = API_RESULT_FIELDS_TO_EXPORT[key];
                    acc[newKey] = value;
                    if (key === 'isActive') {
                        acc[newKey] = value === true ? 1 : 0;
                    }
                    return acc;
                },
                {},
            );
        });
    };

    const filterChangedData = (data) => {
        const [unchanged, changed] = partition(data, (row) => {
            const fieldsToCompare = FIELDS_CHANGE_NEED_VALIDATION.concat(
                FIELDS_CHANGE_NO_NEED_VALIDATION,
            );
            const pickedRow = pick(row, fieldsToCompare);
            const originalRow = lookup.currentRatesLookup[row.id];
            const pickedOriginalRow = pick(originalRow, fieldsToCompare);
            return dataIsUnchangedFilter(pickedRow, pickedOriginalRow);
        });

        const [noNeedValidation, needValidation] = partition(changed, (row) => {
            const fieldsToCompare = FIELDS_CHANGE_NEED_VALIDATION.concat(
                FIELDS_CHANGE_NO_NEED_VALIDATION,
            );
            const pickedRow = pick(row, fieldsToCompare);
            const originalRow = lookup.currentRatesLookup[row.id];
            const pickedOriginalRow = pick(originalRow, fieldsToCompare);
            return dataIsUnchangedFilter(pickedRow, pickedOriginalRow);
        });
        return {
            noNeedValidation,
            needValidation,
            unchanged: unchanged.map((v) => ({
                ...v,
                ignored: true,
            })),
        };
    };

    const verifyRates = (data) => {
        const [ratesExist, ratesNotExist] = partition(data, (row) => {
            // If rate ID is empty, treat it as a new rate (doesn't exist)
            if (!row.id) {
                return false;
            }
            const originData = lookup.currentRatesLookup[row.id];
            return originData;
        });

        const [ratesExistAndActive, ratesNotActive] = partition(
            ratesExist,
            (v) => Boolean(lookup.currentRatesLookup[v.id].isActive),
        );

        const ratesHaveInvalidNumbers = data.filter((row) =>
            RATES.some((rate) => isNegativeRate(row[rate])),
        );
        
        const [ratesValid, ratesInvalid] = partition(ratesNotExist, (row) => {
            // If rate ID is empty, it's a new rate - skip validation and treat as valid
            if (!row.id) {
                return true;
            }
            // Otherwise, validate required fields
            return FIELDS_CHANGE_NEED_VALIDATION.every((field) => Boolean(row[field]));
        });

        return {
            ratesNotActive,
            ratesNotExist,
            ratesValid,
            ratesInvalid,
            ratesExistAndActive,
            ratesHaveInvalidNumbers,
        };
    };

    /**
     * convert invalid JSON data back to CSV with papaparse.
     * */
    const handleExportInvalidData = () => {
        const invalidData = transformJSONToCSV(
            dataJSON.filter(haveErrorsFilter),
        );
        const res = Papa.unparse(invalidData);
        downloadCSV(res, INVALID_DATA_FILENAME);
    };

    const flagDataWithErrors = ({
        noValidationRatesInvalid = [],
        validationRatesInvalid = [],
        invalidRates = [],
        data = [],
    }) => {
        RATE_NOT_ACTIVE;
        PREFIX_ERROR;
        INVALID_PRIORITY_ERROR;
        INVALID_RATE_NAME;
        INVALID_RATE_ERROR;
        // Helper to validate fields and collect errors
        const collectFieldErrors = (entry, fieldsWithErrors) => {
            const errors = new Set();
            if (!entry?.prefix && fieldsWithErrors.includes('prefix'))
                errors.add(PREFIX_ERROR);
            if (!entry?.priority && fieldsWithErrors.includes('priority'))
                errors.add(INVALID_PRIORITY_ERROR);
            if (!entry?.name && fieldsWithErrors.includes('name'))
                errors.add(INVALID_RATE_NAME);
            return Array.from(errors);
        };

        // Collect all errors for the provided invalid rates
        const mapEntriesWithErrors = (
            entries,
            additionalError,
            fieldsWithErrors = [],
        ) => {
            return entries.map((entry) => ({
                ...entry,
                errors: [
                    ...new Set([
                        ...collectFieldErrors(entry, fieldsWithErrors),
                        ...(additionalError ? [additionalError] : []),
                    ]),
                ],
            }));
        };

        // Combine errors from all categories
        const allErrors = [
            ...mapEntriesWithErrors(
                noValidationRatesInvalid,
                null,
                FIELDS_CHANGE_NEED_VALIDATION,
            ),
            ...mapEntriesWithErrors(
                validationRatesInvalid,
                null,
                FIELDS_CHANGE_NEED_VALIDATION,
            ),
            ...mapEntriesWithErrors(invalidRates, INVALID_RATE_ERROR),
        ];

        // Build a lookup for errors by id
        const dataJSONTmp = {};
        allErrors.forEach(({ id, errors }) => {
            if (!dataJSONTmp[id]) {
                dataJSONTmp[id] = [];
            }
            dataJSONTmp[id].push(...errors);
        });

        // Append errors to the original data, give unique errors
        const newDataWithErrors = data.map((entry) => ({
            ...entry,
            errors: [...new Set(dataJSONTmp[entry.id])] || [],
        }));

        return { newDataWithErrors };
    };

    const processCSVData = async () => {
        const { data } = await convertCSVDataToJSON(uploadData);
        changeVerificationProgresses(1);

        const convertedData = convertCSVHeaders(data);
        changeVerificationProgresses(2);

        changeVerificationProgresses(2);
        setDataJSON(convertedData);

        const { noNeedValidation, needValidation, unchanged } =
            filterChangedData(convertedData);
        changeVerificationProgresses(2);
        changeVerificationChecks(2);
        if (noNeedValidation.length < 1 && needValidation.length < 1) {
            setError(NO_DATA_FOUND_ERROR);
        }
        const {
            ratesInvalid: noValidationRatesInvalid,
            ratesNotActive: noValidationRatesNotActive,
            ratesExistAndActive: noValidationRatesOKAndExist,
            ratesHaveInvalidNumbers: noValidationRatesHaveInvalidNumbers,
        } = verifyRates(noNeedValidation);
        changeVerificationProgresses(3);
        const {
            ratesInvalid: validationRatesInvalid,
            ratesNotActive: validationRatesNotActive,
            ratesHaveInvalidNumbers: validationRatesHaveInvalidNumbers,
            ratesValid: validationRatesOK,
        } = verifyRates(needValidation);
        changeVerificationProgresses(3);
        changeVerificationChecks(3);

        const { newDataWithErrors: noNeedValidationWithErrors } =
            flagDataWithErrors({
                noValidationRatesNotActive,
                noValidationRatesInvalid,
                invalidRates: noValidationRatesHaveInvalidNumbers,
                data: noNeedValidation,
            });

        const { newDataWithErrors: needValidationWithErrors } =
            flagDataWithErrors({
                validationRatesNotActive,
                validationRatesInvalid,
                invalidRates: validationRatesHaveInvalidNumbers,
                data: needValidation,
            });
        changeVerificationProgresses(4);
        changeVerificationChecks(4);
        const { allSucceedData } = reportResult({
            noNeedValidationWithErrors,
            needValidationWithErrors,
            unchanged,
        });

        transformDataToPayload({
            data: allSucceedData,
            noValidationRatesOKAndExist,
            validationRatesOK,
        });
    };

    const transformDataToPayload = ({
        data,
        noValidationRatesOKAndExist,
        validationRatesOK,
    }) => {
        const newPayload = data.map((v) => {
            const apiData = lookup.currentRatesLookup[v.id];

            const ratesData = {
                ...apiData,
                validationRatesOK,
                ...v,
            };
            const doesNotNeedLookup = noValidationRatesOKAndExist.find(
                (rate) => rate.id === v.id,
            );
            if (doesNotNeedLookup) {
                return ratesData;
            }
            return {
                ...ratesData,
            };
        });
        setPayloads(newPayload);
    };

    /**
     * once lookups are avaialbe, process our uploaded CSV.
     * */
    useEffect(() => {
        processCSVData();
    }, [lookup]);

    return {
        uploadData,
        handleUploadData,
        verificationChecks,
        handleExportInvalidData,
        loading,
        payloads,
        verificationResult,
        handleReset,
        error,
    };
}
